import { useRef, useState } from "react";
import { api } from "@/lib/site";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Cloudinary signed image uploader. Calls /api/cloudinary/signature (admin only)
 * then uploads file directly to Cloudinary. onUploaded receives secure_url.
 */
export default function ImageUploader({ folder = "uploads/gallery", onUploaded, label = "Upload Image" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB");
      return;
    }
    setBusy(true);
    setProgress(10);
    try {
      const { data: sig } = await api.get("/cloudinary/signature", { params: { folder } });
      setProgress(30);

      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const cloudName = encodeURIComponent(sig.cloud_name);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: "POST",
        body: form,
      });
      setProgress(80);
      const data = await res.json();
      if (!res.ok || !data.secure_url) {
        throw new Error(data?.error?.message || "Upload failed");
      }
      setProgress(100);
      toast.success("Image uploaded");
      onUploaded?.(data.secure_url, data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 800);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-3">
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="image-upload-input" />
      <button
        type="button"
        data-testid="image-upload-btn"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-2 bg-royal hover:bg-gold hover:text-royal-dark text-white px-5 h-11 rounded-full font-semibold transition-all disabled:opacity-60"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        {busy ? `Uploading ${progress}%` : label}
      </button>
    </div>
  );
}
